/**
 * Onglet Admin > OCR Mistral — budget + audit des appels cloud Mistral.
 *
 * Sprint big-PDF V1.3 (2026-05-22). Consomme GET /admin/mistral-usage.
 * Affiche :
 * - KPI cards : mois courant + cumul total (calls, pages, coût EUR)
 * - Barres mensuelles (6 derniers mois) sans dépendance graphique
 * - Top 20 dossiers du mois courant par coût
 * - 50 derniers appels avec dossier_ref + filename
 * - Seuils config (page_count + budget alerte) en pied
 */
import { useEffect, useState, useCallback } from 'react';
import { ApiError, api } from '../../lib/api';
import { toast } from '../../stores/toaster';

type Summary = { calls: number; pages: number; cost_eur: number };

type MonthlyRow = Summary & { month: string };
type DossierRow = Summary & {
  dossier_id: string;
  dossier_ref: string | null;
  dossier_titre: string | null;
};
type CallRow = {
  id: string;
  called_at: string;
  pages_processed: number;
  cost_eur: number;
  latency_ms: number;
  model: string;
  dossier_ref: string | null;
  dossier_titre: string | null;
  filename: string | null;
};

type Payload = {
  current_month: Summary;
  total: Summary;
  monthly: MonthlyRow[];
  recent: CallRow[];
  by_dossier: DossierRow[];
  budget_alert_eur: number;
  threshold_pages: number;
  as_of: string;
};

function fmtEur(v: number): string {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(v);
}

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-BE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtMonth(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-BE', { year: 'numeric', month: 'short' });
}

export default function MistralUsageTab() {
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get<Payload>('/admin/mistral-usage');
      setData(d);
    } catch (err) {
      const detail = err instanceof ApiError ? err.problem.detail || err.problem.title : String(err);
      setError(detail);
      toast.error('Erreur chargement Mistral usage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <p className="text-sm text-text-secondary">Chargement…</p>;
  if (error) return <p className="text-sm text-cat-def">Erreur : {error}</p>;
  if (!data) return <p className="text-sm text-text-secondary">Aucune donnée.</p>;

  const monthMax = Math.max(1, ...data.monthly.map((m) => Number(m.cost_eur)));
  const budgetPct =
    data.budget_alert_eur > 0
      ? Math.min(100, (Number(data.current_month.cost_eur) / data.budget_alert_eur) * 100)
      : 0;
  const budgetWarn = budgetPct >= 80;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiCard
          label="Mois courant"
          calls={data.current_month.calls}
          pages={data.current_month.pages}
          cost={Number(data.current_month.cost_eur)}
          accent={budgetWarn ? 'warn' : 'normal'}
        />
        <KpiCard
          label="Cumul total"
          calls={data.total.calls}
          pages={data.total.pages}
          cost={Number(data.total.cost_eur)}
        />
        <div className="rounded-md border border-rule bg-paper-card p-4">
          <div className="mb-1 text-[11px] uppercase tracking-wider text-text-tertiary">
            Budget mois courant
          </div>
          <div className="mb-2 flex items-baseline gap-2">
            <span className={`text-2xl font-semibold ${budgetWarn ? 'text-cat-def' : 'text-text-primary'}`}>
              {fmtEur(Number(data.current_month.cost_eur))}
            </span>
            <span className="text-xs text-text-secondary">
              / {fmtEur(data.budget_alert_eur)} alerte
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded bg-paper-subtle">
            <div
              className={`h-full transition-all ${budgetWarn ? 'bg-cat-def' : 'bg-accent'}`}
              style={{ width: `${budgetPct}%` }}
            />
          </div>
          <p className="mt-2 text-[10.5px] text-text-tertiary">
            Email Brevo envoyé si seuil dépassé (cron quotidien)
          </p>
        </div>
      </div>

      {/* Barres mensuelles */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-text-primary">6 derniers mois</h2>
        {data.monthly.length === 0 ? (
          <p className="text-[12.5px] italic text-text-tertiary">Aucun appel.</p>
        ) : (
          <div className="space-y-1.5 rounded-md border border-rule bg-paper-card p-3">
            {data.monthly.map((m) => {
              const cost = Number(m.cost_eur);
              const pct = (cost / monthMax) * 100;
              return (
                <div key={m.month} className="flex items-center gap-3 text-[12px]">
                  <span className="w-20 text-text-secondary">{fmtMonth(m.month)}</span>
                  <div className="relative h-4 flex-1 rounded bg-paper-subtle">
                    <div
                      className="absolute inset-y-0 left-0 rounded bg-accent-light"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-24 text-right text-text-primary">{fmtEur(cost)}</span>
                  <span className="w-16 text-right text-text-tertiary">{m.pages} p.</span>
                  <span className="w-14 text-right text-text-tertiary">{m.calls} app.</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Top 20 dossiers du mois */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-text-primary">
          Top dossiers (mois courant)
        </h2>
        {data.by_dossier.length === 0 ? (
          <p className="text-[12.5px] italic text-text-tertiary">Aucun dossier ce mois.</p>
        ) : (
          <div className="overflow-hidden rounded-md border border-rule">
            <table className="w-full text-[12px]">
              <thead className="bg-paper-subtle text-left text-[11px] uppercase tracking-wider text-text-tertiary">
                <tr>
                  <th className="px-3 py-1.5">Ref</th>
                  <th className="px-3 py-1.5">Titre</th>
                  <th className="px-3 py-1.5 text-right">Appels</th>
                  <th className="px-3 py-1.5 text-right">Pages</th>
                  <th className="px-3 py-1.5 text-right">Coût</th>
                </tr>
              </thead>
              <tbody>
                {data.by_dossier.map((d) => (
                  <tr key={d.dossier_id} className="border-t border-rule odd:bg-paper-card">
                    <td className="px-3 py-1.5 font-mono text-[11px]">{d.dossier_ref ?? '—'}</td>
                    <td className="px-3 py-1.5">{d.dossier_titre ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right">{d.calls}</td>
                    <td className="px-3 py-1.5 text-right">{d.pages}</td>
                    <td className="px-3 py-1.5 text-right">{fmtEur(Number(d.cost_eur))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 50 derniers appels */}
      <section>
        <h2 className="mb-2 text-sm font-semibold text-text-primary">50 derniers appels</h2>
        {data.recent.length === 0 ? (
          <p className="text-[12.5px] italic text-text-tertiary">Aucun appel.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto rounded-md border border-rule">
            <table className="w-full text-[11.5px]">
              <thead className="sticky top-0 bg-paper-subtle text-left text-[10.5px] uppercase tracking-wider text-text-tertiary">
                <tr>
                  <th className="px-2 py-1.5">Date</th>
                  <th className="px-2 py-1.5">Dossier</th>
                  <th className="px-2 py-1.5">Fichier</th>
                  <th className="px-2 py-1.5 text-right">Pages</th>
                  <th className="px-2 py-1.5 text-right">Latence</th>
                  <th className="px-2 py-1.5 text-right">Coût</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r.id} className="border-t border-rule odd:bg-paper-card">
                    <td className="whitespace-nowrap px-2 py-1.5 text-text-secondary">
                      {fmtDateTime(r.called_at)}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10.5px]">{r.dossier_ref ?? '—'}</td>
                    <td className="max-w-xs truncate px-2 py-1.5" title={r.filename ?? ''}>
                      {r.filename ?? '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right">{r.pages_processed}</td>
                    <td className="px-2 py-1.5 text-right text-text-tertiary">
                      {Math.round(r.latency_ms / 1000)}s
                    </td>
                    <td className="px-2 py-1.5 text-right">{fmtEur(Number(r.cost_eur))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Config readonly */}
      <section className="text-[11.5px] text-text-tertiary">
        <p>
          Seuil bascule Mistral : <span className="font-mono">{data.threshold_pages}</span> pages
          (env <code>OCR_MISTRAL_THRESHOLD_PAGES</code>) · Seuil alerte budget :{' '}
          <span className="font-mono">{fmtEur(data.budget_alert_eur)}</span>/mois (env{' '}
          <code>MISTRAL_BUDGET_ALERT_EUR</code>) · Snapshot du {fmtDateTime(data.as_of)}.
        </p>
      </section>
    </div>
  );
}

function KpiCard({
  label,
  calls,
  pages,
  cost,
  accent = 'normal',
}: {
  label: string;
  calls: number;
  pages: number;
  cost: number;
  accent?: 'normal' | 'warn';
}) {
  return (
    <div className="rounded-md border border-rule bg-paper-card p-4">
      <div className="mb-1 text-[11px] uppercase tracking-wider text-text-tertiary">{label}</div>
      <div className="mb-1.5">
        <span
          className={`text-2xl font-semibold ${
            accent === 'warn' ? 'text-cat-def' : 'text-text-primary'
          }`}
        >
          {fmtEur(cost)}
        </span>
      </div>
      <div className="text-[12px] text-text-secondary">
        {pages.toLocaleString('fr-BE')} pages · {calls.toLocaleString('fr-BE')} appels
      </div>
    </div>
  );
}
